import { ApiError, get, send } from "../api.js";
import type { CreditEffect, LessonCreditSummary, PaymentSummary } from "./api.js";
import type { RecurrencePlanSummary, RecurrenceStatus } from "./recurrences.js";

function settlementPolicyRouteUnavailable(cause: ApiError): boolean {
  // The Hub preserves FastAPI's string detail, and an unmatched route uses
  // this exact generic message. Handler-level 404s must keep their business
  // reason instead of sending staff toward an unnecessary deployment check.
  return cause.status === 405 || (cause.status === 404 && cause.message === "Not Found");
}

async function sendSettlementPolicyAwareCreation<T>(
  legacyPath: string,
  policyAwarePath: string,
  input: { automatic_settlement_policy?: "settle_outstanding_first" | "no_automatic_backdating" },
  key: string,
): Promise<T> {
  const requiresPolicyAwareBackend =
    input.automatic_settlement_policy === "no_automatic_backdating";
  try {
    return await send<T>(
      "POST",
      requiresPolicyAwareBackend ? policyAwarePath : legacyPath,
      { ...input, idempotency_key: key },
      { "Idempotency-Key": key },
    );
  } catch (cause: unknown) {
    if (
      requiresPolicyAwareBackend &&
      cause instanceof ApiError &&
      settlementPolicyRouteUnavailable(cause)
    ) {
      throw new ApiError(
        cause.status,
        "The booking backend must be updated before credits can be kept from automatic backdating. No block was created.",
        cause.code,
        cause.body,
      );
    }
    throw cause;
  }
}

/**
 * The staff side of the booking system, through the Hub's generic proxy.
 *
 * `/api/staff-api/*` forwards to FastAPI with the caller's own staff token, so
 * what comes back is already scoped: a tutor asking for a report gets their own
 * provider's figures without asking for that, and asking for somebody else's is
 * refused there rather than hidden here. Nothing below narrows a result.
 *
 * **Private and public lessons only.** School lessons are Hub-native since
 * PR #47 and never reach Amelia, so nothing here is on their path —
 * `apps/hub/src/routes/schoolDays.ts` is. `createAppointment` and
 * `providerServices` below currently have no caller: the school-day screen was
 * their only one before the pivot. They are kept rather than deleted because
 * creating a private appointment is still Amelia's job and this is where that
 * would go.
 *
 * Shapes are transcribed from the frozen contract (`openapi/wmf-api.json` in
 * WMF-SIte, authoritative on `main` since the backend release) and checked
 * against it by hand, on a date, by a person.
 *
 * **That check has been wrong twice.** Two operations were added here on
 * 16 August while this comment still asserted every operation was frozen, and
 * separately this side recorded `POST /api/v1/recurrences` as unfrozen for four
 * days after it was frozen. Both failures have the same root: there is no copy
 * of the contract in this repository to check against, so every claim about it
 * is somebody's reading rather than something a test can hold. Handwritten
 * rather than generated is still the right call — a second copy would be a
 * second thing to keep current — but adding a call here means confirming the
 * operation against WMF-SIte and dating it, in the same commit.
 *
 * Where a number arrives as a string it stays a string — those are money, and
 * turning "465.00" into a float on the way to a screen is how a penny goes
 * missing.
 */

export interface ReportingPeriod {
  start_date: string;
  end_date: string;
  evaluated_at: string;
  scope: string;
  provider_id: number | null;
}

/**
 * Where a figure came from, and what it does not cover.
 *
 * Rendered rather than dropped. Confirmed earnings exclude fees, tax and
 * disputes, and the utilisation denominator is each tutor's Amelia schedule as
 * it stands now — a number shown without that note is a number somebody will
 * reconcile against their bank statement.
 */
export interface ReportingDataSource {
  name: string;
  authoritative_for: string[];
  coverage_note: string;
}

export interface ReportBase {
  period: ReportingPeriod;
  data_sources: ReportingDataSource[];
}

export interface RetentionRisk {
  portal_student_id: string;
  display_name: string;
  last_lesson_at: string | null;
  next_lesson_at: string | null;
  days_since_last_lesson: number | null;
  risk_band: string;
}

export interface RetentionBookingPatternReview {
  portal_student_id: string;
  display_name: string;
  last_lesson_at: string;
  next_lesson_at: string;
  completed_lesson_count: number;
  future_booking_count: 1;
}

export interface RetentionTasterFollowUpReview {
  portal_student_id: string;
  display_name: string;
  last_taster_lesson_at: string;
  days_since_taster: number;
}

export interface RetentionCancellationFollowUpReview {
  portal_student_id: string;
  display_name: string;
  cancelled_lesson_at: string;
  days_since_cancellation: number;
}

export interface RetentionUnusedCreditFollowUpReview {
  portal_student_id: string;
  display_name: string;
  last_completed_private_lesson_at: string | null;
  latest_available_credit_purchased_at: string;
  days_since_evidence: number;
}

export interface RetentionReport extends ReportBase {
  registered_students: number;
  active_students: number;
  new_students: number;
  returning_students: number;
  students_without_future_booking: number;
  inactive_14_days: number;
  inactive_30_days: number;
  inactive_60_days: number;
  attention: RetentionRisk[];
  /** Optional while older FastAPI deployments remain compatible. */
  one_upcoming_booking_reviews?: RetentionBookingPatternReview[];
  /** Optional while older FastAPI deployments remain compatible. */
  taster_follow_up_reviews?: RetentionTasterFollowUpReview[];
  /** Optional while older FastAPI deployments remain compatible. */
  cancellation_follow_up_reviews?: RetentionCancellationFollowUpReview[];
  /** Optional while older FastAPI deployments remain compatible. */
  unused_credit_follow_up_reviews?: RetentionUnusedCreditFollowUpReview[];
}

export interface AttendanceReport extends ReportBase {
  total_appointments: number;
  attendance_rate_percent: string | null;
  breakdown: {
    scheduled: number;
    completed: number;
    no_show: number;
    student_cancelled: number;
    tutor_cancelled: number;
    /**
     * Cancelled, with nobody recorded as having done it.
     *
     * Expected to be the *largest* cancellation bucket for now. The Hub mirror
     * has the fact of a cancellation and not the actor, so it sends no outcome
     * and the booking status falls through to here. Shown rather than dropped:
     * two attributed counts that quietly exclude most of the cancellations is a
     * screen somebody quotes in a meeting.
     */
    cancelled_unknown: number;
    other: number;
  };
}

export interface CreditReport extends ReportBase {
  /** Blocks a customer bought. Credits issued without a sale are not sales. */
  blocks_sold: number;
  active_blocks: number;
  credits_purchased: number;
  /** Goodwill, carried-forward, transferred and paid-cancellation credits. */
  blocks_issued_without_sale?: number;
  credits_issued_without_sale?: number;
  outstanding_credits: number;
  expiring_within_30_days: number;
  outstanding_liability: string;
  currency: string;
  transactions: {
    granted: number;
    consumed: number;
    restored: number;
    adjusted_net: number;
    reversed: number;
  };
}

export interface PaymentReport extends ReportBase {
  confirmed_transactions: number;
  confirmed_gross: string;
  refunds: string;
  confirmed_net: string;
  pending_on_site_requests: number;
  past_unpaid_lessons: number;
  currency: string;
  by_method: { payment_method: string; transaction_count: number; amount: string }[];
  /** Present when the read uses the A3 bounded provider-page contract. */
  provider_page?: PaymentProviderPage | null;
}

export interface PaymentProviderRow {
  provider_id: number;
  confirmed_transactions: number;
  confirmed_gross: string;
  refunds: string;
  confirmed_net: string;
  pending_on_site_requests: number;
  past_unpaid_lessons: number;
  by_method: { payment_method: string; transaction_count: number; amount: string }[];
}

export interface PaymentProviderPage {
  items: PaymentProviderRow[];
  total: number;
  page: number;
  limit: number;
  total_pages: number;
  /** Null for an unfiltered organisation report; otherwise the full selection. */
  provider_ids: number[] | null;
}

export type OnSitePaymentMethod = "cash" | "card_terminal" | "bank_transfer";

export interface UnpaidLesson {
  portal_student_id: string;
  student_name: string;
  tutor_name: string;
  service_name: string;
  provider_id: number;
  service_id: number;
  starts_at: string;
  amount_due: string;
  currency: string;
  appointment_id: number;
  payment_id: number;
  payment_state: string;
  payment_method: string | null;
}

export interface UnpaidLessonReport extends ReportBase {
  total: number;
  page?: number;
  limit?: number;
  total_pages?: number;
  items: UnpaidLesson[];
}

export interface EarningsReport extends ReportBase {
  currency: string;
  coverage: string;
  providers: {
    provider_id: number;
    confirmed_transactions: number;
    confirmed_gross: string;
    refunds: string;
    confirmed_net: string;
  }[];
}

export interface UtilisationReport extends ReportBase {
  providers: {
    provider_id: number;
    appointment_count: number;
    booked_minutes: number;
    teaching_days: number;
    average_appointments_per_teaching_day: string;
    timetable_gap_minutes: number;
    available_minutes: number | null;
    /** Confirmed private minutes overlapping an effective Amelia working interval. */
    in_schedule_booked_minutes?: number | null;
    availability_coverage?:
      | "current_schedule"
      | "current_schedule_incomplete"
      | "current_schedule_projected"
      | "historical_unknown";
    availability_source_read_at?: string | null;
    utilisation_rate_percent: string | null;
  }[];
}

export interface OperationalException {
  code: string;
  severity: string;
  entity_type: string;
  entity_id: string;
  provider_id: number | null;
  portal_student_id: string | null;
  needs_attention?: boolean;
  move_operation_id?: string | null;
  appointment_id?: number | null;
  message: string;
  occurred_at: string;
}

export interface AppointmentMoveCheck {
  operation_id: string;
  appointment_id: number;
  needs_attention: boolean;
  observed_start: string;
  requested_start: string | null;
  message: string;
}

export type AppointmentMoveResolution = Omit<AppointmentMoveCheck, "observed_start" | "requested_start">;

export interface OperationalExceptionReport extends ReportBase {
  total: number;
  actionable_total?: number;
  page?: number;
  limit?: number;
  total_pages?: number;
  items: OperationalException[];
}

export interface OnSiteRequest {
  id: string;
  portal_student_id: string;
  /** Required since 13 August, populated from the stored request. */
  student_name: string;
  appointment_id: number;
  booking_id: number;
  provider_id: number;
  service_id: number;
  starts_at: string;
  status: string;
  payment: {
    state: string;
    method: string;
    amount_total: string;
    amount_due: string;
    currency: string;
  };
}

export interface LessonRequestActivity {
  tutor_first_viewed_at: string | null;
  tutor_last_viewed_at: string | null;
  viewers: Array<{ staff_user_id: string; display_name: string; responsible_tutor: boolean;
    first_viewed_at: string; last_viewed_at: string; view_count: number }>;
  decided_at: string | null;
  decided_by_staff_id: string | null;
  decided_by_name: string | null;
  decision_reason: string | null;
}

export interface LessonRequestHistoryItem extends OnSiteRequest {
  activity: LessonRequestActivity;
}

export interface LessonRequestHistoryQuery {
  provider_id?: string;
  status?: string;
  date_field: "requested" | "lesson" | "decision";
  from_date?: string;
  to_date?: string;
  limit: string;
  offset: string;
}

export type CancellationCreditResolution = "restore" | "consume";
export type PaidCancellationAction = "grant_rebooking_credit" | "retain_payment";

/**
 * FastAPI's decision about what cancelling does to the student's entitlement.
 *
 * `existing_credit` is the lesson's own used credit (Return / Keep). A
 * `paid_cancellation` lesson never used a credit: it was paid for directly and
 * can earn one new rebooking credit (Issue / Keep payment). The Hub renders
 * this; it never works out from payment strings which applies.
 */
export type CancellationEntitlementKind =
  | "existing_credit"
  | "paid_cancellation"
  | "none"
  | "requires_review";

export interface CancellationEntitlement {
  kind: CancellationEntitlementKind;
  credits: number;
  decision_required: boolean;
  automatic_action: CancellationCreditResolution | PaidCancellationAction | null;
  available_actions: Array<CancellationCreditResolution | PaidCancellationAction>;
  payment_classification?: string | null;
}

export interface BulkCancelCreditChoice {
  appointment_id: number;
  credit_resolution?: CancellationCreditResolution;
  paid_cancellation_action?: PaidCancellationAction;
}

export interface BulkCancelPreview {
  requested_count: number;
  cancellable_count: number;
  already_cancelled_count: number;
  blocked_count: number;
  credits_to_restore: number;
  credit_resolution_required_count: number;
  credits_to_grant?: number;
  paid_decision_required_count?: number;
  items: {
    appointment_id: number;
    provider_id: number | null;
    booking_start: string | null;
    current_status: string | null;
    credits_to_restore: number;
    notice_period_hours: number | null;
    credit_resolution_required: boolean;
    available_credit_resolutions: CancellationCreditResolution[];
    existing_credit_resolution: CancellationCreditResolution | null;
    entitlement?: CancellationEntitlement | null;
    can_cancel: boolean;
    message: string | null;
  }[];
}

export interface TutorCancellationPreview {
  appointment_id: number;
  starts_at: string;
  notice_period_hours: number;
  notice_required_hours: number;
  credit_resolution_required: boolean;
  automatic_credit_resolution: CancellationCreditResolution | null;
  available_credit_resolutions: CancellationCreditResolution[];
  credits_to_restore: number;
  entitlement?: CancellationEntitlement | null;
}

export interface TutorCancellationResult {
  appointment: { id: number; status: string };
  credits_restored: number;
  /** New rebooking credits for a paid lesson — never counted as restored. */
  credits_granted?: number;
  granted_lesson_block_id?: string | null;
  /** Missing while an older FastAPI version is still serving the Hub. */
  entitlement_kind?: CancellationEntitlementKind;
  paid_cancellation_action?: PaidCancellationAction | null;
  credit_resolution: CancellationCreditResolution | null;
  credit_resolution_status: "applied" | "requires_attention";
  credits_remaining: number | null;
  credit_review_required?: boolean;
}

export interface BulkReschedulePreview {
  requested_count: number;
  reschedulable_count: number;
  already_at_target_count: number;
  blocked_count: number;
  items: {
    appointment_id: number;
    provider_id: number | null;
    current_start: string | null;
    requested_start: string;
    can_reschedule: boolean;
    already_at_target: boolean;
    message: string | null;
    /** Present when WMF-SIte classified a refusal as a safe schedule-policy exception. */
    working_hours_override_available?: boolean;
    schedule_override_available?: boolean;
  }[];
}

export interface BulkRescheduleResult {
  requested_count: number;
  rescheduled_count: number;
  already_at_target_count: number;
  failed_count: number;
  unknown_count?: number;
  results: {
    appointment_id: number;
    success: boolean;
    final_start: string | null;
    /** True when this was a retry that found the work already done. */
    replayed: boolean;
    message: string | null;
    outcome_unknown?: boolean;
    booking_stage?: string | null;
    upstream_status?: number | null;
    override_diagnostics?: Record<string, string> | null;
    request_reference?: string | null;
  }[];
}

export interface BulkCancelResult {
  requested_count: number;
  cancelled_count: number;
  already_cancelled_count: number;
  failed_count: number;
  credits_restored: number;
  credits_granted?: number;
  results: {
    appointment_id: number;
    success: boolean;
    final_status: string | null;
    credits_restored: number;
    credits_granted?: number;
    granted_lesson_block_id?: string | null;
    entitlement_kind?: CancellationEntitlementKind;
    paid_cancellation_action?: PaidCancellationAction | null;
    credit_resolution: CancellationCreditResolution | null;
    credit_review_required: boolean;
    replayed: boolean;
    message: string | null;
  }[];
}

export interface BulkCancelOperation {
  idempotency_key: string;
  state: "pending" | "completed";
  result: BulkCancelResult | null;
}

export interface RecurrenceSeriesOccurrence {
  amelia_appointment_id: number;
  starts_at: string;
}

/** Response shape documented by WMF-SIte's L20 series lookup endpoint. */
export interface RecurrenceSeriesFromAppointment {
  recurrence_plan_id: string;
  plan_status: RecurrenceStatus;
  provider_id: number;
  following: RecurrenceSeriesOccurrence[];
  truncated?: boolean | null;
}

export interface CreatedAppointment {
  id: number;
  booking_start: string;
  booking_end: string;
  provider_id: number;
  service_id: number;
  status: string;
}

export interface ProviderService {
  id: number;
  name: string;
  duration: number | null;
  price: string | number | null;
  status: string | null;
}

/** Availability returned by WMF-SIte's staff contract. */
export interface StaffAvailability {
  service_id: number;
  provider_id: number;
  minimum: string | null;
  maximum: string | null;
  slots: { starts_at: string; provider_id: number }[];
}

/** Safe result shape for the staff student booking contract. */
export interface StaffLessonBookingResult {
  idempotency_key: string;
  replayed: boolean;
  appointment_id?: number | null;
  booking_id?: number | null;
  portal_student_id: string;
  provider_id: number;
  service_id: number;
  starts_at: string;
  booking_status: string;
  attendance_outcome: string;
  credit: CreditEffect;
  payment: PaymentSummary;
  mirror_sync: { state: "refreshed" | "queued" | "pending_retry" };
  evaluated_at: string;
}

/**
 * A repeating booking.
 *
 * Load-bearing for "cancel all future lessons": the plan is what lets that
 * screen name an active recurrence *before* the cancellation and ask about
 * stopping it *after*, instead of quietly treating the two as one act. Raised
 * as the last gap in the frozen contract and closed there — `listRecurrencePlans`
 * and `updateRecurrencePlanStatus` are both in `openapi/wmf-api.json` now.
 */
export interface RecurrencePlan {
  id: string;
  portal_student_id: string | null;
  student_name: string | null;
  provider_id: number;
  service_id: number;
  starts_on: string;
  start_time: string;
  ends_on: string | null;
  cadence: string;
  status: string;
}

/** A report's window and how wide to look. Both go straight to FastAPI. */
export interface ReportQuery {
  start_date: string;
  end_date: string;
  scope: "mine" | "organisation";
  page?: number;
  limit?: number;
  student?: string;
  status?: string;
  exclude_pending_requests?: boolean;
  /** Keep student-level retention detail out of aggregate dashboard reads. */
  include_attention?: boolean;
  /**
   * One member of staff, by their Amelia provider id.
   *
   * Absent is the ordinary case and means the whole of whatever `scope` allows.
   * Present narrows the answer to one person's lessons, which is what the staff
   * filter on the reports page asks for — one request per person ticked, rather
   * than one whole-school answer somebody has to divide up by eye.
   *
   * Sent to FastAPI and **checked in the answer**, see `byProvider` below.
   */
  provider_id?: number;
}

export interface PaymentReportQuery extends ReportQuery {
  /** Repeated `provider_ids` query parameters, as defined by WMF-SIte A3. */
  provider_ids?: readonly number[];
}

const reportParams = (query: ReportQuery) => ({
  start_date: query.start_date,
  end_date: query.end_date,
  scope: query.scope,
  ...(query.provider_id === undefined ? {} : { provider_id: String(query.provider_id) }),
  ...(query.page === undefined ? {} : { page: String(query.page) }),
  ...(query.limit === undefined ? {} : { limit: String(query.limit) }),
  ...(query.student === undefined ? {} : { student: query.student }),
  ...(query.status === undefined ? {} : { status: query.status }),
  ...(query.exclude_pending_requests === undefined
    ? {}
    : { exclude_pending_requests: String(query.exclude_pending_requests) }),
  ...(query.include_attention === undefined
    ? {}
    : { include_attention: String(query.include_attention) }),
});

const report = <T>(name: string, query: ReportQuery) =>
  get<T>(`/staff-api/api/v1/reports/${name}`, reportParams(query));

const paymentReport = (query: PaymentReportQuery) =>
  get<PaymentReport>("/staff-api/api/v1/reports/payments", {
    ...reportParams(query),
    ...(query.provider_ids === undefined
      ? {}
      : { provider_ids: query.provider_ids.map(String) }),
  });

/** One person's answer, kept beside who it is about. */
export interface PersonReport<T> {
  providerId: number;
  report: T;
}

/**
 * What is said when a report comes back wider than it was asked for.
 *
 * Written for whoever is reading the screen rather than for whoever would fix
 * it, because the first of those is standing in front of it: the reports that
 * *can* be broken down by person are named, so there is something to do next.
 */
export const NOT_NARROWED =
  "This report came back for the whole school even though it was asked about particular staff, so it is left blank rather than showing everybody's figures under one person's name. Earnings and Timetable do break down by person; for this one, tick Everyone.";

/**
 * Ask a report about each person in turn, and refuse an answer about everybody.
 *
 * The narrowing itself is FastAPI's — it holds the money and the credits, and
 * it computes every one of these from a snapshot it fetches from the Hub with
 * the same `provider_id` — so this side cannot do the sums again if the far
 * side declines. What it can do is notice, which is the whole point of this
 * function: every report echoes back the `period` it answered for, so an answer
 * whose `provider_id` is not the one that was asked for is an answer about the
 * wrong people, and is thrown away rather than drawn under somebody's name.
 *
 * That is a deliberately blunt rule. A tutor's earnings shown against a
 * colleague's name is not a display bug; it is a number that gets read out in a
 * meeting, and there is no version of it that is better than a blank panel
 * saying why it is blank.
 *
 * The requests go together rather than one after another. Six reports about
 * four people is twenty-four calls if each waits its turn, and the reports page
 * is already six panels loading at once.
 */
export async function byProvider<T extends ReportBase>(
  providers: readonly number[],
  load: (providerId: number) => Promise<T>,
): Promise<PersonReport<T>[]> {
  return Promise.all(
    providers.map(async (providerId) => {
      const answer = await load(providerId).catch((cause: unknown) => {
        // A refusal of the parameter itself reads, upstream, as an invalid
        // request — which to somebody who has just ticked a name is the page
        // blaming them for a box it offered. Said in our own words instead.
        if (cause instanceof ApiError && (cause.status === 400 || cause.status === 422)) {
          throw new Error(NOT_NARROWED);
        }
        throw cause;
      });

      if (answer.period.provider_id !== providerId) throw new Error(NOT_NARROWED);
      return { providerId, report: answer };
    }),
  );
}

/**
 * Coupon delivery: added 24 August 2026, `GET /api/v1/lesson-blocks/{id}` on
 * `main`. Only the fields the delivery-status screen reads are transcribed
 * here, not the full lesson-block/coupon shape.
 */
export type LessonBlockCouponDeliveryStatus = "pending" | "retryable" | "sent" | "requires_attention";

export interface LessonBlockCouponDelivery {
  status: LessonBlockCouponDeliveryStatus;
  recipient_email: string;
  attempt_count: number;
  sent_at: string | null;
  last_attempted_at: string | null;
  last_error: string | null;
}

export interface LessonBlockCouponRestoration {
  id: string;
  idempotency_key: string;
  amelia_appointment_id: number;
  amelia_booking_id: number;
  reason: string;
  restored_uses: number;
  status: "pending" | "applied" | "requires_attention";
  previous_limit: number | null;
  new_limit: number | null;
  attempted_at: string | null;
  applied_at: string | null;
  last_error: string | null;
  created_at: string;
}

/**
 * A website block whose coupon still expires before the block does.
 *
 * The backend moves the coupon with the block's expiry; when it could not,
 * saving the same expiry again retries it, so that save is not skipped.
 */
export function couponLagsBlock(detail: LessonBlockDetail): boolean {
  const coupon = detail.coupon;
  if (!coupon?.follows_ledger || coupon.status !== "issued") return false;
  return new Date(coupon.expires_at).getTime() !== new Date(detail.block.expires_at).getTime();
}

export interface LessonBlockCouponInfo {
  code: string;
  status: "pending" | "issued" | "requires_attention" | "retired";
  initial_limit: number;
  current_limit: number;
  expires_at: string;
  recipient_email: string;
  delivery: LessonBlockCouponDelivery | null;
  restorations: LessonBlockCouponRestoration[];
  /**
   * The backend lowers or hides this coupon in Amelia itself when a refund,
   * closure or balance correction removes credits. Absent from older backends,
   * which leave that to staff.
   */
  follows_ledger?: boolean;
}

export type LessonBlockSource =
  | "amelia"
  | "staff"
  | "legacy_migration"
  | "goodwill"
  | "transfer"
  | "paid_cancellation";
export type LessonBlockPaymentMethod =
  | "stripe"
  | "cash"
  | "card_terminal"
  | "bank_transfer"
  | "other";

export interface LessonBlockProduct {
  id: string;
  display_name: string;
  amelia_block_service_id: number;
  amelia_lesson_service_id: number;
  credit_quantity: number;
  is_active: boolean;
}

/** A named Amelia service, used to keep the settings UI free of raw ids. */
export interface CatalogueService {
  id: number;
  name: string;
  duration: number | null;
  status: string | null;
}

export interface CatalogueServicePage {
  page: number;
  items: CatalogueService[];
  count_filtered: number;
  count_total: number;
}

/** Read the complete paginated catalogue through the existing staff proxy. */
export async function allCatalogueServices(): Promise<CatalogueService[]> {
  const services: CatalogueService[] = [];
  let page = 1;
  while (true) {
    const result = await staffBooking.catalogueServices(page);
    services.push(...result.items);
    if (services.length >= result.count_filtered || result.items.length === 0) return services;
    page += 1;
  }
}

export interface AutomationJob {
  id: string;
  slug: string;
  display_name: string;
  handler_name: string;
  provider_id: number;
  /** Added by the Hub from the one current, unambiguous tutor profile, if any. */
  provider_name?: string | null;
  is_enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface AutomationRun {
  id: string;
  job_id: string;
  mode: "preview" | "apply";
  trigger: string;
  status: string;
  proposed_count: number;
  applied_count: number;
  warning_count: number;
  error_count: number;
  summary: Record<string, unknown>;
  error_message: string | null;
  started_at: string;
  completed_at: string | null;
}

export interface AutomationRunPage {
  items: AutomationRun[];
  page: number;
  limit: number;
  total: number;
  has_more: boolean;
}

export interface AutomationBlockProposal {
  external_event_id: string;
  provider_id: number;
  block_date: string;
  start_time: string | null;
  end_time: string | null;
  reason: string;
  source_payload: Record<string, unknown>;
}

export interface AutomationBlockPlan {
  proposal: AutomationBlockProposal;
  action: "create" | "update" | "unchanged" | "review";
  managed_block_id: string | null;
  existing_status: string | null;
  applied: boolean;
  conflict_count: number;
  warning: string | null;
  error: string | null;
}

export interface AutomationExecution {
  run: AutomationRun;
  blocks: AutomationBlockPlan[];
  missing_managed_event_ids: string[];
}

export interface AutomationManagedBlock {
  id: string;
  external_event_id: string;
  provider_id: number;
  block_date: string;
  start_time: string | null;
  end_time: string | null;
  reason: string;
  status: string;
  last_error: string | null;
  applied_at: string | null;
  retired_at: string | null;
}

export interface LessonCreditTransaction {
  id: string;
  idempotency_key: string;
  related_transaction_id: string | null;
  transaction_type: "grant" | "use" | "restore" | "adjustment" | "reversal";
  credit_delta: number;
  amelia_appointment_id: number | null;
  amelia_booking_id: number | null;
  notes: string;
  created_by_staff_id: string | null;
  created_at: string;
}

export interface LessonBlockRefund {
  id: string;
  idempotency_key: string;
  credits_refunded: number;
  amount_refunded: string;
  suggested_amount: string;
  payment_currency: string;
  refund_method: LessonBlockPaymentMethod;
  external_reference: string | null;
  reason: string;
  refunded_at: string;
  recorded_by_staff_id: string | null;
  created_at: string;
}

export interface LessonBlockDetail {
  reconciliation?: {
    status: "completed" | "incomplete" | "failed" | "not_applicable";
    credits_applied: number | null;
    same_block_reuse_skipped: number;
    settled_lessons?: Array<{
      amelia_appointment_id: number;
      amelia_booking_id: number;
      starts_at: string;
      lesson_block_id: string;
    }>;
    message: string | null;
  } | null;
  block: {
    id: string;
    portal_student_id: string | null;
    student_name: string | null;
    provider_id: number;
    credits_purchased: number;
    payment_amount: string;
    payment_currency: string;
    payment_method: LessonBlockPaymentMethod;
    source: LessonBlockSource;
    status: "active" | "cancelled" | "refunded" | "requires_attention";
    redemption_authority?: "local_ledger" | "amelia_coupon" | "migration_hold" | "claim_hold";
    automatic_settlement_policy?: "settle_outstanding_first" | "no_automatic_backdating" | null;
    paid_at: string;
    expires_at: string;
    notes: string;
    created_by_staff_id: string | null;
    created_at: string;
    updated_at: string;
  };
  product: LessonBlockProduct;
  coupon: LessonBlockCouponInfo | null;
  balance: number;
  available_balance: number;
  is_expired: boolean;
  transactions: LessonCreditTransaction[];
  refunds: LessonBlockRefund[];
}

export interface LessonBlockList {
  blocks: LessonBlockDetail[];
  total: number;
  limit: number;
  offset: number;
}

export type LessonBlockView = "all" | "current" | "history";

export interface LessonBlockRefundPreview {
  lesson_block_id: string;
  credits_purchased: number;
  credits_remaining: number;
  credits_to_refund: number;
  unit_amount: string;
  suggested_amount: string;
  payment_currency: string;
  total_previously_refunded: string;
}

export const staffBooking = {
  /**
   * Staff use the same WMF-SIte credit read model as families, through the
   * staff token. FastAPI applies the tutor's provider scope; the Hub does not
   * calculate or filter the returned balance.
   */
  lessonCredits: (studentId: string) =>
    get<LessonCreditSummary>(
      `/staff-api/api/v1/student-booking/students/${encodeURIComponent(studentId)}/lesson-credits`,
    ),
  /** Full staff read model used by the narrowly scoped 4C correction panel. */
  lessonBlocks: (
    studentId: string,
    options: {
      view?: LessonBlockView;
      limit?: number;
      offset?: number;
    } = {},
  ) =>
    get<LessonBlockList>("/staff-api/api/v1/lesson-blocks", {
      portal_student_id: studentId,
      limit: String(options.limit ?? 100),
      offset: options.offset === undefined ? undefined : String(options.offset),
      view: options.view,
    }),
  lessonBlockProducts: () =>
    get<LessonBlockProduct[]>("/staff-api/api/v1/lesson-blocks/products"),
  lessonBlockProductsForSettings: () =>
    get<LessonBlockProduct[]>("/staff-api/api/v1/lesson-blocks/products", { include_inactive: "true" }),
  unmappedLessonBlockServices: () =>
    get<CatalogueService[]>("/staff-api/api/v1/lesson-blocks/products/unmapped-services"),
  catalogueServices: (page: number) =>
    get<CatalogueServicePage>("/staff-api/api/v1/catalogue/services", { page: String(page) }),
  createLessonBlockProduct: (input: Omit<LessonBlockProduct, "id">) =>
    send<LessonBlockProduct>("POST", "/staff-api/api/v1/lesson-blocks/products", input),
  updateLessonBlockProduct: (
    productId: string,
    input: Partial<Omit<LessonBlockProduct, "id" | "amelia_block_service_id">>,
  ) =>
    send<LessonBlockProduct>(
      "PATCH",
      `/staff-api/api/v1/lesson-blocks/products/${encodeURIComponent(productId)}`,
      input,
    ),
  automations: () => get<AutomationJob[]>("/staff-api/api/v1/automations"),
  automation: (jobId: string) =>
    get<AutomationJob>(`/staff-api/api/v1/automations/${encodeURIComponent(jobId)}`),
  automationRuns: (jobId: string, page = 1, limit = 15) =>
    get<AutomationRunPage>(`/staff-api/api/v1/automations/${encodeURIComponent(jobId)}/runs`, {
      page: String(page),
      limit: String(limit),
    }),
  automationBlocks: (jobId: string) =>
    get<AutomationManagedBlock[]>(`/staff-api/api/v1/automations/${encodeURIComponent(jobId)}/blocks`),
  setAutomationEnabled: (jobId: string, is_enabled: boolean) =>
    send<AutomationJob>(
      "PATCH",
      `/staff-api/api/v1/automations/${encodeURIComponent(jobId)}/status`,
      { is_enabled },
    ),
  /** Preview and Apply intentionally share one FastAPI endpoint; only mode differs. */
  runAutomation: (jobId: string, mode: "preview" | "apply") =>
    send<AutomationExecution>(
      "POST",
      `/staff-api/api/v1/automations/${encodeURIComponent(jobId)}/runs`,
      { mode },
    ),
  /** Read Amelia-backed availability through WMF-SIte; the Hub never calls Amelia. */
  availability: (params: {
    provider_id: string;
    service_id: string;
    start_date: string;
    end_date: string;
  }) => get<StaffAvailability>("/staff-api/api/v1/availability", params),
  /**
   * Book one lesson against WMF-SIte's authoritative credit-funded staff flow.
   *
   * `overrideWorkingHours` is the compatibility parameter name for an
   * explicit one-lesson schedule-policy override. WMF-SIte revalidates it
   * immediately before Amelia changes.
   */
  creditFundedBooking: (
    studentId: string,
    body: { provider_id: number; service_id: number; starts_at: string; notify_participants?: boolean },
    key: string,
    overrideWorkingHours = false,
  ) =>
    send<StaffLessonBookingResult>(
      "POST",
      `/staff-api/api/v1/staff-booking/students/${encodeURIComponent(studentId)}/appointments/credit-funded`,
      { ...body, notify_participants: body.notify_participants ?? false, override_working_hours: overrideWorkingHours },
      { "Idempotency-Key": key },
    ),
  providerServices: (providerId: number) =>
    get<ProviderService[]>(`/staff-api/api/v1/catalogue/employees/${providerId}/services`),
  /** Whether, and to whom, a lesson block's coupon email actually sent. */
  lessonBlockDetail: (lessonBlockId: string) =>
    get<LessonBlockDetail>(`/staff-api/api/v1/lesson-blocks/${encodeURIComponent(lessonBlockId)}`),
  recordLegacyMigration: (
    input: {
      portal_student_id: string;
      provider_id: number;
      block_service_id: number;
      quantity: number;
      migration_date: string;
      reason: string;
      old_system_reference?: string | null;
    },
    key: string,
  ) =>
    send<LessonBlockDetail>(
      "POST",
      "/staff-api/api/v1/lesson-blocks/legacy-migrations",
      { ...input, idempotency_key: key },
      { "Idempotency-Key": key },
    ),
  adjustExceptionalCredit: (
    lessonBlockId: string,
    input: { credit_delta: number; reason: string; original_transaction_id?: string | null },
    key: string,
  ) =>
    send<LessonBlockDetail>(
      "POST",
      `/staff-api/api/v1/lesson-blocks/${encodeURIComponent(lessonBlockId)}/exceptional-credit-adjustments`,
      { ...input, idempotency_key: key },
      { "Idempotency-Key": key },
    ),
  grantGoodwillCredit: (
    input: {
      portal_student_id: string;
      provider_id: number;
      block_service_id: number;
      quantity: number;
      reason: string;
      automatic_settlement_policy?: "settle_outstanding_first" | "no_automatic_backdating";
    },
    key: string,
  ) =>
    sendSettlementPolicyAwareCreation<LessonBlockDetail>(
      "/staff-api/api/v1/lesson-blocks/goodwill-credits",
      "/staff-api/api/v1/lesson-blocks/goodwill-credits/with-automatic-settlement-policy",
      input,
      key,
    ),
  restoreCredit: (
    lessonBlockId: string,
    input: { amelia_appointment_id: number; portal_student_id: string; notes: string },
    key: string,
  ) =>
    send<LessonBlockDetail>(
      "POST",
      `/staff-api/api/v1/lesson-blocks/${encodeURIComponent(lessonBlockId)}/credits/restore`,
      { ...input, idempotency_key: key },
      { "Idempotency-Key": key },
    ),
  restoreCouponUse: (
    lessonBlockId: string,
    input: { appointment_id: number; booking_id: number; reason: string },
    key: string,
  ) =>
    send<LessonBlockDetail>(
      "POST",
      `/staff-api/api/v1/lesson-blocks/${encodeURIComponent(lessonBlockId)}/coupon/restorations`,
      { ...input, idempotency_key: key },
      { "Idempotency-Key": key },
    ),
  extendLessonBlockExpiry: (
    lessonBlockId: string,
    input: { expires_at: string; reason: string },
    key: string,
  ) =>
    send<LessonBlockDetail>(
      "PATCH",
      `/staff-api/api/v1/lesson-blocks/${encodeURIComponent(lessonBlockId)}/expiry`,
      input,
      { "Idempotency-Key": key },
    ),
  /**
   * Expiry is the one 4C endpoint whose current FastAPI schema has no JSON
   * idempotency key. Re-read the absolute target before and after an ambiguous
   * failure so an exact retry never appends a second expiry-history entry.
   */
  ensureLessonBlockExpiry: async (
    lessonBlockId: string,
    input: { expires_at: string; reason: string },
    key: string,
  ) => {
    const isTarget = (detail: LessonBlockDetail) =>
      new Date(detail.block.expires_at).getTime() === new Date(input.expires_at).getTime() &&
      !couponLagsBlock(detail);
    const path = `/staff-api/api/v1/lesson-blocks/${encodeURIComponent(lessonBlockId)}`;
    const before = await get<LessonBlockDetail>(path);
    if (isTarget(before)) return before;
    try {
      return await send<LessonBlockDetail>("PATCH", `${path}/expiry`, input, {
        "Idempotency-Key": key,
      });
    } catch (cause: unknown) {
      const after = await get<LessonBlockDetail>(path);
      if (isTarget(after)) return after;
      throw cause;
    }
  },
  refundPreview: (lessonBlockId: string, creditsToRefund?: number) =>
    send<LessonBlockRefundPreview>(
      "POST",
      `/staff-api/api/v1/lesson-blocks/${encodeURIComponent(lessonBlockId)}/refund/preview`,
      creditsToRefund === undefined ? {} : { credits_to_refund: creditsToRefund },
    ),
  recordExternalRefund: (
    lessonBlockId: string,
    input: {
      credits_refunded: number;
      amount_refunded: string;
      refund_method: LessonBlockPaymentMethod;
      external_reference?: string | null;
      reason: string;
      refunded_at: string;
      confirmed_externally: true;
    },
    key: string,
  ) =>
    send<LessonBlockDetail>(
      "POST",
      `/staff-api/api/v1/lesson-blocks/${encodeURIComponent(lessonBlockId)}/refunds`,
      { ...input, idempotency_key: key },
      { "Idempotency-Key": key },
    ),
  createAppointment: (input: {
    portal_student_id: string;
    student_name: string;
    provider_id: number;
    service_id: number;
    starts_at: string;
    persons?: number;
  }, key: string) =>
    send<CreatedAppointment>(
      "POST",
      "/staff-api/api/v1/appointments",
      {
        ...input,
        persons: input.persons ?? 1,
        status: "approved",
        notify_participants: false,
        slot_validation: "staff_scheduled",
      },
      { "Idempotency-Key": key },
    ),
  markPaymentReceived: (
    appointmentId: number,
    paymentId: number,
    input: {
      portal_student_id: string;
      amount: string;
      payment_method: OnSitePaymentMethod;
      paid_at: string;
    },
    key: string,
  ) =>
    send<unknown>(
      "PATCH",
      `/staff-api/api/v1/appointments/${appointmentId}/payments/${paymentId}/paid`,
      input,
      { "Idempotency-Key": key },
    ),
  /**
   * The paired reversal of `markPaymentReceived`: move a payment that was
   * marked paid by mistake back to pending. Audited on the FastAPI side; it
   * refunds no money and adds, removes or returns no lesson credits. Contract:
   * WMF-SIte#205, confirmed 2026-08-31.
   */
  markPaymentPending: (
    appointmentId: number,
    paymentId: number,
    input: {
      portal_student_id: string;
    },
    key: string,
  ) =>
    send<unknown>(
      "PATCH",
      `/staff-api/api/v1/appointments/${appointmentId}/payments/${paymentId}/pending`,
      input,
      { "Idempotency-Key": key },
    ),
  registerLessonBlock: (
    input: {
      portal_student_id: string;
      student_name: string;
      provider_id: number;
      block_service_id: number;
      payment_amount: string;
      payment_method: OnSitePaymentMethod;
      paid_at?: string;
      notes: string;
      automatic_settlement_policy: "settle_outstanding_first" | "no_automatic_backdating";
    },
    key: string,
  ) =>
    sendSettlementPolicyAwareCreation<LessonBlockDetail>(
      "/staff-api/api/v1/lesson-blocks",
      "/staff-api/api/v1/lesson-blocks/with-automatic-settlement-policy",
      input,
      key,
    ),

  retention: (query: ReportQuery) => report<RetentionReport>("student-retention", query),
  attendance: (query: ReportQuery) => report<AttendanceReport>("attendance", query),
  credits: (query: ReportQuery) => report<CreditReport>("credits", query),
  payments: (query: PaymentReportQuery) => paymentReport(query),
  unpaidLessons: (query: ReportQuery) => report<UnpaidLessonReport>("unpaid-lessons", query),
  earnings: (query: ReportQuery) => report<EarningsReport>("earnings", query),
  utilisation: (query: ReportQuery) => report<UtilisationReport>("utilisation", query),
  exceptions: (query: ReportQuery) =>
    report<OperationalExceptionReport>("operational-exceptions", query),

  lessonRequests: (status?: string, page = 1, limit = 15, scope?: "mine" | "organisation") =>
    get<{
      requests: OnSiteRequest[];
      total: number;
      limit?: number;
      offset?: number;
    }>(scope ? "/admin/action-centre/requests" : "/staff-api/api/v1/student-booking/lesson-requests", {
      status,
      ...(scope ? { scope, page: String(page) } : {}),
      limit: String(limit),
      offset: String((page - 1) * limit),
    }),

  /**
   * `notify_participants` is sent on every decision rather than left to the
   * contract's default of `true`. A family waiting on an answer should hear it,
   * so the default is right — but a screen that depends on somebody else's
   * default changes behaviour the day they change their mind, and this one is
   * a reply to a person.
   */
  approveRequest: (id: string, reason: string | undefined, key: string, notify: boolean) =>
    send<OnSiteRequest>(
      "POST",
      `/staff-api/api/v1/student-booking/lesson-requests/${encodeURIComponent(id)}/approve`,
      { ...(reason !== undefined ? { reason } : {}), notify_participants: notify },
      { "Idempotency-Key": key },
    ),

  // Checked against WMF-SIte's runtime contract on 15 October 2026. Deploy its
  // scoped history/view endpoints before releasing this Requests UI.
  requestHistory: (query: LessonRequestHistoryQuery) =>
    get<{ requests: LessonRequestHistoryItem[]; total: number; limit: number; offset: number }>(
      "/staff-api/api/v1/student-booking/lesson-request-history", { ...query },
    ),

  viewRequest: (id: string, key: string) =>
    send<LessonRequestActivity>("POST",
      `/staff-api/api/v1/student-booking/lesson-requests/${encodeURIComponent(id)}/views`,
      { idempotency_key: key }, { "Idempotency-Key": key }),

  rejectRequest: (id: string, reason: string | undefined, key: string, notify: boolean) =>
    send<OnSiteRequest>(
      "POST",
      `/staff-api/api/v1/student-booking/lesson-requests/${encodeURIComponent(id)}/reject`,
      { ...(reason !== undefined ? { reason } : {}), notify_participants: notify },
      { "Idempotency-Key": key },
    ),

  bulkCancelPreview: (appointmentIds: number[], reason: string) =>
    send<BulkCancelPreview>("POST", "/staff-api/api/v1/appointments/bulk-cancel/preview", {
      appointment_ids: appointmentIds,
      reason,
    }),

  bulkCancelOperation: (key: string) =>
    get<BulkCancelOperation>(`/staff-api/api/v1/appointments/bulk-cancel/operations/${encodeURIComponent(key)}`),

  bulkCancel: (
    appointmentIds: number[],
    reason: string,
    key: string,
    notify: boolean,
    creditResolutions: BulkCancelCreditChoice[] = [],
  ) =>
    send<BulkCancelResult>(
      "POST",
      "/staff-api/api/v1/appointments/bulk-cancel",
      {
        appointment_ids: appointmentIds,
        reason,
        notify_participants: notify,
        credit_resolutions: creditResolutions,
      },
      { "Idempotency-Key": key },
    ),

  cancellationPreview: (appointmentId: string) =>
    send<TutorCancellationPreview>(
      "POST",
      `/staff-api/api/v1/appointments/${encodeURIComponent(appointmentId)}/cancellation-preview`,
    ),

  cancelAppointment: (
    appointmentId: string,
    creditResolution: CancellationCreditResolution | null,
    key: string,
    notifyParticipants: boolean,
    paidAction: PaidCancellationAction | null = null,
  ) =>
    send<TutorCancellationResult>(
      "PATCH",
      `/staff-api/api/v1/appointments/${encodeURIComponent(appointmentId)}/status`,
      {
        status: "canceled",
        notify_participants: notifyParticipants,
        ...(creditResolution ? { credit_resolution: creditResolution } : {}),
        ...(paidAction ? { paid_cancellation_action: paidAction } : {}),
      },
      { "Idempotency-Key": key },
    ),

  bulkReschedulePreview: (
    items: { appointment_id: number; starts_at: string }[],
    reason: string,
    overrideWorkingHours = false,
  ) =>
    send<BulkReschedulePreview>("POST", "/staff-api/api/v1/appointments/bulk-reschedule/preview", {
      items,
      reason,
      override_working_hours: overrideWorkingHours,
    }),

  bulkReschedule: (
    items: { appointment_id: number; starts_at: string }[],
    reason: string,
    key: string,
    notify: boolean,
    overrideWorkingHours = false,
  ) =>
    send<BulkRescheduleResult>(
      "POST",
      "/staff-api/api/v1/appointments/bulk-reschedule",
      {
        items,
        reason,
        notify_participants: notify,
        override_working_hours: overrideWorkingHours,
      },
      { "Idempotency-Key": key },
    ),

  checkMove: (operationId: string, appointmentId: number) =>
    send<AppointmentMoveCheck>(
      "POST",
      `/staff-api/api/v1/appointments/bulk-reschedule/${encodeURIComponent(operationId)}/appointments/${appointmentId}/check`,
      {},
    ),

  resolveMove: (operationId: string, appointmentId: number, note: string) =>
    send<AppointmentMoveResolution>(
      "POST",
      `/staff-api/api/v1/appointments/bulk-reschedule/${encodeURIComponent(operationId)}/appointments/${appointmentId}/resolve`,
      { note },
    ),

  /**
   * Checked 20 October 2026 against WMF-SIte main ef53a249993354f6788e5d30259a543116217153:
   * listRecurrencePlans accepts portal_student_id and optional status; omission
   * returns every status without pagination, with count === items.length.
   * See docs/PENDING-RELEASE.md, Student Overview recurring plans, for source evidence.
   */
  recurrences: (portalStudentId: string, status?: RecurrenceStatus) =>
    get<{ items: RecurrencePlanSummary[]; count: number }>("/staff-api/api/v1/recurrences", {
      portal_student_id: portalStudentId,
      ...(status ? { status } : {}),
    }),

  seriesFromAppointment: (ameliaAppointmentId: number) =>
    get<RecurrenceSeriesFromAppointment>(
      `/staff-api/api/v1/recurrences/by-appointment/${encodeURIComponent(String(ameliaAppointmentId))}`,
    ),

  stopRecurrence: (planId: string) =>
    send<unknown>("PATCH", `/staff-api/api/v1/recurrences/${encodeURIComponent(planId)}/status`, {
      status: "cancelled",
    }),
};

/**
 * The phone-call list, gathered from every person who was asked about.
 *
 * Deduplicated by student, because the list is one to work through and a child
 * with two teachers is still one phone call. Their row says the same thing on
 * whichever tutor's report it arrived on — when they were last taught and what
 * is booked — so the first one seen is kept rather than merged field by field.
 *
 * Ordered by how long it has been, which the single-tutor list arrives in
 * already and four of them interleaved do not. A student with no last lesson at
 * all sorts as nought rather than as for ever: they have never been taught, so
 * "it has been ages" is not what their row says.
 */
export function mergeAttention(lists: readonly RetentionRisk[][]): RetentionRisk[] {
  const seen = new Map<string, RetentionRisk>();
  for (const list of lists) {
    for (const student of list) {
      if (!seen.has(student.portal_student_id)) seen.set(student.portal_student_id, student);
    }
  }
  return [...seen.values()].sort(
    (a, b) => (b.days_since_last_lesson ?? 0) - (a.days_since_last_lesson ?? 0),
  );
}

/** The Hub's own list of a student's future lessons, for the bulk workflow. */
export interface FutureLesson {
  ameliaAppointmentId: number;
  ameliaBookingId: string;
  startsAt: string;
  endsAt: string;
  serviceName: string | null;
  tutorId: string | null;
  tutorName: string | null;
  status: string;
  /** Other people on the same appointment. Cancelling takes their lesson too. */
  sharedWith: number;
}

/** A student's future lessons, for the bulk workflow. */
export const futureLessons = (studentId: string) =>
  get<{
    student: { id: string; fullName: string };
    lessons: FutureLesson[];
    truncated: boolean;
    /**
     * Lessons ahead that were left out because they are the Hub's own.
     *
     * A school day booked here has no Amelia appointment id, so neither bulk
     * action can name it. Non-zero with an empty `lessons` is the case worth
     * wording carefully: the student has lessons ahead, and none of them can be
     * moved or cancelled from this page.
     */
    hubNative: number;
    limit: number;
  }>(`/admin/students/${encodeURIComponent(studentId)}/future-lessons`);
